<script setup>
import {ref, onMounted, computed, reactive, watch} from 'vue'
import {useI18n} from "vue-i18n";
import {
    getClientState, getVarTemplates,
} from "@/services/api/attributeLayout.js";
import {getAttribute, getAttributeById} from "@/services/api/attribute.js";
import {FormLayout} from "@/enums/enums.js";
import EntityForm from "@/components/common/Form.vue";
import {LayoutEntity} from "@/entities/LayoutEntity.js";
import {searchAndSetOptions} from "@/utils/index.js";
import SkeletonLoader from "@/components/common/SkeletonLoader.vue";

const props = defineProps({
    formValue: {type: Number, required: true},
});
const { t } = useI18n();
const isFetching = ref(false);
const emit = defineEmits(['add-item'])
const form = ref(null);
const loading = ref(null);
let counter = 0;
const layoutEntity = new LayoutEntity();
const entity = ref(layoutEntity);
const formData = ref(entity.value.getDefaultValues());
watch(
    () => props.formValue,
    (newVal) => {
        entity.value.fields = layoutEntity.fields.map(field => {
            if (field.key === "radiusOperatorListCheck") {
                return { ...field, editable: newVal === FormLayout.Check, visible: newVal === FormLayout.Check };
            }
            if (field.key === "radiusOperatorListReply") {
                return { ...field, editable: newVal  === FormLayout.Reply, visible: newVal === FormLayout.Reply };
            }
            return field;
        });
        const newDefaults = entity.value.getDefaultValues();
        formData.value = {
            ...newDefaults,
            ...formData.value
        };
    },
    { immediate: true, deep: true }
);


const generateId = () => `${Date.now()}-${counter++}`

const emitItem = async () => {
    const { valid } = await form.value.validate();
    if (!valid) return;
    formData.value['value'] = cleanMixed(formData.value['value']);

    const newItem = {
        id: generateId(),
        ...formData.value,
        st_check_reply: props.formValue === FormLayout.Check ? "Check" : "Reply"
    };
    resetForm();
    emit('add-item', newItem)
}

function cleanMixed(input) {
    if (typeof input === "string") {
        return input.trim();
    }

    if (!Array.isArray(input)) {
        return "";
    }

    const cleaned = input
        .flatMap(item => {
            if (!item) return [];

            if (typeof item === "string") {
                return item
                    .split(",")
                    .map(v => v.trim())
                    .filter(v => v !== "");
            }

            if (typeof item === "object") {
                return item.value ?? item.title ?? "";
            }

            return [];
        })
        .map(v => String(v).trim())
        .filter(v => v !== "");

    return cleaned.join(", ");
}

const resetForm = () => {
    const keysWithDefault = entity.value.getKeysWithDefaultValues();

    for (const field of entity.value.fields) {
        if (!keysWithDefault.includes(field.key)) {
            formData.value[field.key] = null;
        } else {
            const defaultField = entity.value.fields.find(f => f.key === field.key);
            formData.value[field.key] = defaultField?.default ?? formData.value[field.key];
        }
    }
};

async function handleSearch({ handler, query }) {
    const handlers = {
        handleSearchAttribute: () =>
            searchAndSetOptions({
                key: "attribute",
                apiFn: getAttribute,
                query,
                entity,
                isFetching
            }),
    };

    if (handlers[handler]) {
        await handlers[handler]();
    } else {
        console.warn(`Handler não encontrado: ${handler}`);
    }
}
watch(
     () => formData.value['attribute'],
    async (newVal) => {
        if (newVal && newVal !== '') {
            const attributeResponse = await getAttributeById({id: newVal.value})
            formData.value['value'] = attributeResponse.data.default_value;
        }
    }
)
onMounted(async () => {
    try {
        loading.value = true;

        const fields = entity.value.fields;

        const [states, varTemplates] = await Promise.all([
            getClientState(),
            getVarTemplates()
        ]);

        const stateField = fields.find(f => f.key === "clientaccessState");
        if (stateField) {
            stateField.options = states.map(state => ({
                value: state,
                label: t(`common.labels.${state}`)
            }));
        }

        const varTemplateField = fields.find(f => f.key === "value");
        if (varTemplateField) {
            varTemplateField.options = varTemplates.map(item => ({
                label: `{{${item.name}}}`,
                value:  `{{${item.name}}}`
            }));
        }

        Object.keys(formData.value).forEach(key => {
            if (key === "value" && formData.value[key] === "") {
                formData.value[key] = null;
            }
        });

    } catch (error) {
        console.error("Erro ao carregar opções iniciais:", error);
    } finally {
        loading.value = false;
    }
});

</script>
<template>
    <div  v-if="!loading"  class="row bg-white">
        <EntityForm :entity="entity" ref="form" v-model="formData" @search="handleSearch" />
        <div class="col-12 d-flex justify-end">
            <v-btn
                color="primary"
                @click="emitItem"
            >
                {{ t('common.labels.add')}}
            </v-btn>
        </div>
    </div>
    <SkeletonLoader v-else page="list" />
</template>



